/**
 * @pj/sudoku — deterministic sudoku generation for the Level-2 "Bhaav" quest.
 *
 * Pure and seedable (mulberry32 from @pj/rules) so the sim and the UI derive
 * the *same* puzzle from the seed stored in the save — the grid survives
 * reloads, and the sim stays the authority on "solved". Generation digs from a
 * full board while a backtracking counter proves the solution stays unique;
 * low given-counts (~26) put it firmly in the "hard" band.
 *
 * Grid encoding: 81-char strings, row-major, '1'-'9' filled, '0' empty.
 */
import { makeRng } from "@pj/rules";

export const GRID_CELLS = 81;
export const HARD_GIVENS = 26;

export interface SudokuPuzzle {
  /** The clues: 81 chars, '0' where the player must fill. */
  givens: string;
  /** The unique solution: 81 chars, all '1'-'9'. */
  solution: string;
}

const rowOf = (i: number): number => Math.floor(i / 9);
const colOf = (i: number): number => i % 9;
const boxOf = (i: number): number => Math.floor(rowOf(i) / 3) * 3 + Math.floor(colOf(i) / 3);

/** Peer cell indices (same row/col/box) for every cell, precomputed once. */
const PEERS: number[][] = (() => {
  const peers: number[][] = [];
  for (let i = 0; i < GRID_CELLS; i++) {
    const set = new Set<number>();
    for (let j = 0; j < GRID_CELLS; j++) {
      if (j === i) continue;
      if (rowOf(j) === rowOf(i) || colOf(j) === colOf(i) || boxOf(j) === boxOf(i)) set.add(j);
    }
    peers.push([...set]);
  }
  return peers;
})();

function candidatesAt(board: Uint8Array, i: number): number[] {
  const used = new Set<number>();
  for (const p of PEERS[i]!) {
    const v = board[p]!;
    if (v) used.add(v);
  }
  const out: number[] = [];
  for (let d = 1; d <= 9; d++) if (!used.has(d)) out.push(d);
  return out;
}

/** Most-constrained empty cell (MRV) — keeps backtracking shallow. */
function pickCell(board: Uint8Array): { cell: number; cands: number[] } | null {
  let best = -1;
  let bestCands: number[] | null = null;
  for (let i = 0; i < GRID_CELLS; i++) {
    if (board[i]) continue;
    const cands = candidatesAt(board, i);
    if (!bestCands || cands.length < bestCands.length) {
      best = i;
      bestCands = cands;
      if (cands.length <= 1) break;
    }
  }
  return bestCands ? { cell: best, cands: bestCands } : null;
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

/** Fill the (partial) board into a complete valid grid. Mutates; true on success. */
function fillBoard(board: Uint8Array, rng: () => number): boolean {
  const next = pickCell(board);
  if (!next) return true; // no empty cells — done
  const { cell, cands } = next;
  shuffle(cands, rng);
  for (const d of cands) {
    board[cell] = d;
    if (fillBoard(board, rng)) return true;
  }
  board[cell] = 0;
  return false;
}

/** Count solutions, stopping early at `limit` (2 is enough for uniqueness). */
export function countSolutions(board: Uint8Array, limit = 2): number {
  const next = pickCell(board);
  if (!next) return 1;
  const { cell, cands } = next;
  let count = 0;
  for (const d of cands) {
    board[cell] = d;
    count += countSolutions(board, limit - count);
    board[cell] = 0;
    if (count >= limit) return count;
  }
  return count;
}

function toString(board: Uint8Array): string {
  let s = "";
  for (let i = 0; i < GRID_CELLS; i++) s += String(board[i]);
  return s;
}

export function fromString(grid: string): Uint8Array {
  const board = new Uint8Array(GRID_CELLS);
  for (let i = 0; i < GRID_CELLS; i++) {
    const c = grid.charCodeAt(i) - 48;
    board[i] = c >= 1 && c <= 9 ? c : 0;
  }
  return board;
}

/**
 * Generate a hard puzzle deterministically from `seed`: build a full solution,
 * then dig cells (in seeded order) while uniqueness holds, down to ~`targetGivens`.
 */
export function generateSudoku(seed: number, targetGivens = HARD_GIVENS): SudokuPuzzle {
  const rng = makeRng(seed);
  const board = new Uint8Array(GRID_CELLS);
  fillBoard(board, rng);
  const solution = toString(board);

  const order = shuffle(
    Array.from({ length: GRID_CELLS }, (_, i) => i),
    rng,
  );
  let givens = GRID_CELLS;
  for (const i of order) {
    if (givens <= targetGivens) break;
    const d = board[i]!;
    board[i] = 0;
    if (countSolutions(board) === 1) givens--;
    else board[i] = d;
  }
  return { givens: toString(board), solution };
}

/** A grid string is a legal state of this puzzle: right shape, givens intact. */
export function entriesValid(puzzle: SudokuPuzzle, entries: string): boolean {
  if (entries.length !== GRID_CELLS) return false;
  for (let i = 0; i < GRID_CELLS; i++) {
    const c = entries.charCodeAt(i) - 48;
    if (c < 0 || c > 9) return false;
    const given = puzzle.givens.charCodeAt(i) - 48;
    if (given !== 0 && c !== given) return false;
  }
  return true;
}

/** The grid is completely and correctly filled. */
export function isSolved(puzzle: SudokuPuzzle, entries: string): boolean {
  return entries === puzzle.solution;
}
